import { COMMUNITY_BASE_PATH, COMMUNITY_REPOSITORY, COMMUNITY_SITE_ORIGIN } from "../../shared/config.js";
import type { Locale } from "../types.js";

export { COMMUNITY_REPOSITORY, COMMUNITY_SITE_ORIGIN };

export type Route =
  | { readonly kind: "locale-gate" }
  | { readonly kind: "themes"; readonly locale: Locale }
  | { readonly kind: "theme"; readonly locale: Locale; readonly themeId: string }
  | { readonly kind: "version"; readonly locale: Locale; readonly themeId: string; readonly version: string }
  | { readonly kind: "guide"; readonly locale: Locale; readonly page: "install" | "submit" | "report" }
  | { readonly kind: "not-found"; readonly locale: Locale };

export function basePath(): string {
  if (typeof document !== "undefined") return document.documentElement.dataset.basePath || "/";
  return COMMUNITY_BASE_PATH;
}

export function siteHref(path: string): string {
  const base = basePath();
  const normalized = path.replace(/^\/+/, "");
  return `${base}${normalized}`.replace(/\/+/g, "/");
}

export function localeHref(locale: Locale, page = "themes"): string {
  return siteHref(`${locale}/${page}`);
}

export function themeHref(locale: Locale, themeId: string): string {
  return localeHref(locale, `themes/${encodeURIComponent(themeId)}`);
}

export function versionHref(locale: Locale, themeId: string, version: string): string {
  return localeHref(locale, `themes/${encodeURIComponent(themeId)}/versions/${encodeURIComponent(version)}`);
}

export function assetHref(remoteUrl: string): string {
  const remote = new URL(remoteUrl);
  const productionPrefix = new URL(`${COMMUNITY_SITE_ORIGIN}/`).pathname;
  if (!remote.pathname.startsWith(productionPrefix)) return remoteUrl;
  return siteHref(remote.pathname.slice(productionPrefix.length));
}

function removeBase(pathname: string): string {
  const known = [basePath(), COMMUNITY_BASE_PATH]
    .map((entry) => entry.replace(/\/+$/, ""))
    .filter((entry) => entry && entry !== "/");
  const prefix = known.find((entry) => pathname === entry || pathname.startsWith(`${entry}/`));
  return (prefix ? pathname.slice(prefix.length) : pathname).replace(/^\/+|\/+$/g, "");
}

export function parseRoute(pathname: string): Route {
  const path = removeBase(pathname);
  if (!path) return { kind: "locale-gate" };
  let parts: string[];
  try {
    parts = path.split("/").map(decodeURIComponent);
  } catch (error) {
    if (!(error instanceof URIError)) throw error;
    return { kind: "not-found", locale: "zh-CN" };
  }
  const locale: Locale = parts[0] === "en" ? "en" : "zh-CN";
  if (parts[0] !== "en" && parts[0] !== "zh-CN") return { kind: "not-found", locale };
  if (parts.length === 2 && parts[1] === "themes") return { kind: "themes", locale };
  if (parts.length === 3 && parts[1] === "themes" && parts[2]) {
    return { kind: "theme", locale, themeId: parts[2] };
  }
  if (parts.length === 5 && parts[1] === "themes" && parts[3] === "versions" && parts[2] && parts[4]) {
    return { kind: "version", locale, themeId: parts[2], version: parts[4] };
  }
  if (parts.length === 2 && ["install", "submit", "report"].includes(parts[1] ?? "")) {
    return { kind: "guide", locale, page: parts[1] as "install" | "submit" | "report" };
  }
  return { kind: "not-found", locale };
}

export function switchLocaleHref(route: Route, locale: Locale): string {
  switch (route.kind) {
    case "locale-gate": return localeHref(locale);
    case "themes": return localeHref(locale);
    case "theme": return themeHref(locale, route.themeId);
    case "version": return versionHref(locale, route.themeId, route.version);
    case "guide": return localeHref(locale, route.page);
    case "not-found": return localeHref(locale);
  }
}
